import {
  AdminSwitch,
  Disclaimer,
  Div,
  getUserPermissions,
  H5,
  lightTheme,
  P,
  reportError,
  Separator,
  setUserPermissions,
  SuccessToast,
  UserPermission,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { SectionCard } from '../../Verifications/components/SectionCard';

/**
 * Grants support-conversation access to a single internal user.
 *
 * Scope note: these two permissions gate SUPPORT threads only. Direct messages
 * and groups are gated by membership, and creating a group is gated by the
 * admin role - none of that is togglable here.
 *
 * Owns its own GET/PUT rather than joining the shared edit modal: that modal is
 * reused by the agent page and saves through one `updateUserById` call, so
 * adding a second independent PUT behind the same button would give a
 * non-transactional save with two failure modes.
 *
 * `PUT /v1/users/:id/permissions` is a full replace - every call sends the
 * complete list the user should end up with, never a delta.
 */
export const UserPermissions = ({ userId }: { userId: string | number }) => {
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getUserPermissions(userId);
        setPermissions(data.data.permissions);
      } catch (error) {
        reportError(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const canRead = permissions.includes(UserPermission.conversationRead);
  const canWrite = permissions.includes(UserPermission.conversationWrite);

  const save = async (next: UserPermission[]) => {
    const previous = permissions;
    setPermissions(next);
    try {
      setSaving(true);
      const { data } = await setUserPermissions(userId, { permissions: next });
      setPermissions(data.data.permissions);
      SuccessToast(data.message);
    } catch (error) {
      setPermissions(previous);
      reportError(error);
    } finally {
      setSaving(false);
    }
  };

  /**
   * The two permissions aren't independent: write without read is a dead
   * state (you could post into a thread you can't open), so granting write
   * grants read, and revoking read revokes write.
   */
  const toggleRead = () =>
    save(canRead ? [] : [UserPermission.conversationRead]);

  const toggleWrite = () =>
    save(
      canWrite
        ? [UserPermission.conversationRead]
        : [UserPermission.conversationRead, UserPermission.conversationWrite]
    );

  if (loading) return null;

  const rows = [
    {
      title: 'Read',
      description: 'See the chat menu and open support threads',
      checked: canRead,
      onChange: toggleRead,
    },
    {
      title: 'Write',
      description: 'Reply to merchants and start new threads',
      checked: canWrite,
      onChange: toggleWrite,
    },
  ];

  return (
    <SectionCard title="Chat Access">
      <Disclaimer $m="0" $color={lightTheme.lightGray}>
        Takes effect the next time this user signs in.
      </Disclaimer>

      {rows.map(({ title, description, checked, onChange }, i) => (
        <div key={title}>
          {i > 0 && <Separator />}
          <Div $flex $alignCenter $between $gap="16px" $p="16px 0">
            <Div>
              <H5 $mb="4">{title}</H5>
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                {description}
              </Disclaimer>
            </Div>
            <AdminSwitch
              checked={checked}
              disabled={saving}
              onChange={onChange}
            />
          </Div>
        </div>
      ))}

      {!canRead && !canWrite && (
        <P $m="0" $color={lightTheme.lightGray}>
          No chat access.
        </P>
      )}
    </SectionCard>
  );
};
